import {
  Box,
  Button,
  Link,
  Text,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import {
  ConfidenceWebsiteLinkEncore,
  LoadingOverlay,
} from '@spotify/confidence-core-react';

import { TransitionScreen } from '../TransitionScreen';
import {
  CreateAccountFormProvider,
  type SignupInvite,
  useCreateAccountFormContext,
} from './CreateAccountFormProvider';
import { ExperimentationLoader } from './ExperimentationLoader';
import { WorkspaceSetupForm } from './WorkspaceSetupForm';
import {
  type CreatedAccount,
  SignupSessionExpiredError,
  expiredSessionMessage,
} from './hooks';

import styles from './CreateAccountForm.module.css';

const contactSupportLink = (
  <Link
    href="mailto:confidence-support@spotify.com"
    target="_blank"
    rel="noopener noreferrer"
  >
    Contact support <VisuallyHidden>(opens in new window)</VisuallyHidden>
  </Link>
);

const verificationEmailHelperText = (
  <>
    Didn&apos;t receive the verification email? Check your spam folder or{' '}
    {contactSupportLink}.
  </>
);

const InviteEmailMismatchDescription = ({
  inviteEmail,
  currentUserEmail,
}: {
  inviteEmail?: string;
  currentUserEmail?: string;
}) => {
  if (!inviteEmail) {
    return (
      <>
        We couldn&apos;t confirm the email address for this invite. Ask the
        sender for a new invite.
      </>
    );
  }
  if (currentUserEmail) {
    return (
      <>
        This invite was sent to a different email address, but you&apos;re
        signed in as <span data-csr-mask>{currentUserEmail}</span>. Switch
        accounts to continue.
      </>
    );
  }
  return (
    <>
      We couldn&apos;t confirm the email address on your account. Sign in with
      the email address that received this invite to continue.
    </>
  );
};

const CreateAccountFormContent = () => {
  const {
    screen,
    showOverlay,
    error,
    handleResultDismissed,
    recheckEmailVerification,
    handleLogin,
    handleLogout,
    currentUserEmail,
    inviteEmail,
    emailVerificationStatus,
  } = useCreateAccountFormContext();

  const verificationStatusText = {
    idle: undefined,
    checking: 'Checking your email verification status…',
    pending:
      "We haven't detected verification yet. Wait a moment, then try again.",
    error: "We couldn't check your verification status. Try again.",
    sign_in_required: expiredSessionMessage,
  }[emailVerificationStatus];

  const isSignInRequired = emailVerificationStatus === 'sign_in_required';
  let verificationActionLabel = "I've verified my email";

  if (emailVerificationStatus === 'checking') {
    verificationActionLabel = 'Checking…';
  } else if (isSignInRequired) {
    verificationActionLabel = 'Sign in again';
  }

  switch (screen) {
    case 'loading':
      return <LoadingOverlay loading />;

    case 'verify_email':
      return (
        <TransitionScreen
          title="Verify your email address"
          description={
            <>
              We sent a verification link to{' '}
              {currentUserEmail ? (
                <span data-csr-mask>{currentUserEmail}</span>
              ) : (
                'your email'
              )}
              . Open it to continue.
              {verificationStatusText && (
                <Text
                  as="div"
                  variant="bodySmall"
                  semanticColor={
                    emailVerificationStatus === 'error'
                      ? 'textNegative'
                      : 'textSubdued'
                  }
                  role="status"
                >
                  {verificationStatusText}
                </Text>
              )}
            </>
          }
          actions={
            <>
              <Button
                variant="primary"
                size="medium"
                disabled={emailVerificationStatus === 'checking'}
                onClick={
                  isSignInRequired ? handleLogout : recheckEmailVerification
                }
              >
                {verificationActionLabel}
              </Button>
              {!isSignInRequired && (
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={handleLogout}
                >
                  Use a different account
                </Button>
              )}
            </>
          }
          helperText={verificationEmailHelperText}
        />
      );

    case 'under_review':
      return (
        <TransitionScreen
          title="Thank you for signing up for Confidence"
          description="We are currently reviewing your account request. You will receive an email once it has been approved."
          helperText={<>Have questions? {contactSupportLink}.</>}
        />
      );

    case 'workspace_setup':
      return (
        <>
          {showOverlay && (
            <div className={styles.overlay}>
              <ExperimentationLoader
                error={error}
                onResultDismissed={handleResultDismissed}
                onSignInAgain={
                  error instanceof SignupSessionExpiredError
                    ? handleLogout
                    : undefined
                }
              />
            </div>
          )}
          <WorkspaceSetupForm />
        </>
      );

    case 'invite_email_mismatch':
      return (
        <TransitionScreen
          title="Use the email address from your invite"
          description={
            <InviteEmailMismatchDescription
              inviteEmail={inviteEmail}
              currentUserEmail={currentUserEmail}
            />
          }
          actions={
            <Button variant="primary" size="medium" onClick={handleLogout}>
              Use a different account
            </Button>
          }
          helperText={<>Need help? {contactSupportLink}.</>}
        />
      );

    case 'login_verify_email':
      return (
        <div data-track-id="region.verify-email" data-track-impression>
          <TransitionScreen
            title="Verify your email address"
            description="Open the verification link we sent to your email, then continue."
            actions={
              <Button variant="primary" size="medium" onClick={handleLogin}>
                Continue after verifying
              </Button>
            }
            helperText={verificationEmailHelperText}
          />
        </div>
      );

    case 'welcome':
      return (
        <div data-track-id="region.welcome" data-track-impression>
          <TransitionScreen
            title="Welcome to Confidence"
            description={
              inviteEmail
                ? 'Continue with the email address that received this invite to join this workspace.'
                : 'Use your work e-mail address, not a personal account.'
            }
            actions={
              <Button
                data-testid="signup-continue"
                variant="primary"
                size="medium"
                onClick={handleLogin}
              >
                Continue
              </Button>
            }
            helperText={
              <>
                <strong>Already using Confidence in your company?</strong>
                <br />
                Ask a teammate for an invite or go to the{' '}
                <Link href="/login">login page</Link> if single sign-on is
                enabled.
                <Box paddingBlockStart="tighter">
                  For more information about how Spotify processes your data,
                  please read our{' '}
                  <ConfidenceWebsiteLinkEncore route="/privacy-policy">
                    Privacy Policy
                  </ConfidenceWebsiteLinkEncore>
                  .
                </Box>
              </>
            }
          />
        </div>
      );

    default: {
      const _exhaustive: never = screen;
      return _exhaustive;
    }
  }
};

export const CreateAccountForm = ({
  invite,
  inviteId,
  onCreate,
}: {
  inviteId?: string;
  invite?: SignupInvite;
  onCreate: (account?: CreatedAccount) => void;
}) => (
  <CreateAccountFormProvider
    invite={invite}
    inviteId={inviteId}
    onCreate={onCreate}
  >
    <CreateAccountFormContent />
  </CreateAccountFormProvider>
);
